import { extractText } from "../chat/message-extract";
import type { GatewayBrowserClient } from "../gateway";
import { generateUUID } from "../uuid";
import type { ChatAttachment } from "../ui-types";

export type ChatState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  chatLoading: boolean;
  chatMessages: unknown[];
  chatThinkingLevel: string | null;
  chatSending: boolean;
  chatMessage: string;
  chatAttachments: ChatAttachment[];
  chatRunId: string | null;
  chatStream: string | null;
  chatStreamStartedAt: number | null;
  lastError: string | null;
};

export type ChatEventPayload = {
  runId: string;
  sessionKey: string;
  state: "delta" | "final" | "aborted" | "error";
  message?: unknown;
  errorMessage?: string;
};

export async function loadChatHistory(state: ChatState) {
  if (!state.client || !state.connected) return;
  state.chatLoading = true;
  state.lastError = null;
  try {
    const res = (await state.client.request("chat.history", {
      sessionKey: state.sessionKey,
      limit: 200,
    })) as { messages?: unknown[]; thinkingLevel?: string | null };
    state.chatMessages = Array.isArray(res.messages) ? res.messages : [];
    state.chatThinkingLevel = res.thinkingLevel ?? null;
  } catch (err) {
    state.lastError = String(err);
  } finally {
    state.chatLoading = false;
  }
}

function dataUrlToBase64(dataUrl: string): { content: string; mimeType: string } | null {
  const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!match) return null;
  return { mimeType: match[1], content: match[2] };
}

export async function sendChatMessage(
  state: ChatState,
  message: string,
  attachments?: ChatAttachment[],
): Promise<boolean> {
  if (!state.client || !state.connected) return false;
  const msg = message.trim();
  const hasAttachments = attachments && attachments.length > 0;
  if (!msg && !hasAttachments) return false;

  const now = Date.now();

  // Build user message content blocks
  const contentBlocks: Array<{ type: string; text?: string; source?: unknown }> = [];
  if (msg) {
    contentBlocks.push({ type: "text", text: msg });
  }
  // Add attachments to the message for display
  if (hasAttachments) {
    for (const att of attachments) {
      if (att.isDocument) {
        // Documents are already uploaded — show a text note
        contentBlocks.push({
          type: "text",
          text: `[Uploaded: ${att.fileName ?? "document"}]`,
        });
      } else {
        // Images: inline preview
        contentBlocks.push({
          type: "image",
          source: { type: "base64", media_type: att.mimeType, data: att.dataUrl },
        });
      }
    }
  }

  state.chatMessages = [
    ...state.chatMessages,
    {
      role: "user",
      content: contentBlocks,
      timestamp: now,
    },
  ];

  state.chatSending = true;
  state.lastError = null;
  const runId = generateUUID();
  state.chatRunId = runId;
  state.chatStream = "";
  state.chatStreamStartedAt = now;

  // Convert image attachments to API format (documents are already on the server)
  const imageAttachments = hasAttachments
    ? attachments.filter((att) => !att.isDocument)
    : [];
  const apiAttachments = imageAttachments.length > 0
    ? imageAttachments
        .map((att) => {
          const parsed = dataUrlToBase64(att.dataUrl);
          if (!parsed) return null;
          return {
            type: "image",
            mimeType: parsed.mimeType,
            content: parsed.content,
          };
        })
        .filter((a): a is NonNullable<typeof a> => a !== null)
    : undefined;

  // Append document filenames to the message so the agent knows about uploaded files
  const docAttachments = hasAttachments
    ? attachments.filter((att) => att.isDocument)
    : [];
  let finalMsg = msg;
  if (docAttachments.length > 0) {
    const docNotes = docAttachments
      .map((att) => `[User uploaded: ${att.fileName ?? "document"}. Use memory_recall to search its indexed content, or file_read to read the raw file from workspace/uploads/.]`)
      .join("\n");
    finalMsg = finalMsg ? `${finalMsg}\n\n${docNotes}` : docNotes;
  }

  try {
    await state.client.request("chat.send", {
      sessionKey: state.sessionKey,
      message: finalMsg,
      deliver: false,
      idempotencyKey: runId,
      attachments: apiAttachments,
    });
    return true;
  } catch (err) {
    const error = String(err);
    state.chatRunId = null;
    state.chatStream = null;
    state.chatStreamStartedAt = null;

    // Map known error codes to friendly messages
    let displayMessage: string;
    if (error.includes("too quickly") || error.includes("RATE_LIMITED")) {
      displayMessage = "You're sending messages too quickly. Please wait a moment before trying again.";
    } else if (error.includes("daily usage limit") || error.includes("BUDGET_EXCEEDED")) {
      displayMessage = "You've reached your daily usage limit. Your limit resets at midnight UTC.";
    } else if (error.includes("unable to continue") || error.includes("SESSION_BLOCKED")) {
      displayMessage = "This conversation has been paused for safety. Please start a new session or contact support.";
    } else if (error.includes("MESSAGE_BLOCKED") || error.includes("blocked")) {
      displayMessage = "Your message couldn't be sent. Please try rephrasing.";
    } else {
      displayMessage = "Something went wrong. Please try again.";
    }

    state.lastError = displayMessage;
    state.chatMessages = [
      ...state.chatMessages,
      {
        role: "assistant",
        content: [{ type: "text", text: displayMessage }],
        timestamp: Date.now(),
      },
    ];
    return false;
  } finally {
    state.chatSending = false;
  }
}

export type UploadResult = {
  filename: string;
  size: number;
  mime_type: string;
  indexed_chunks: number;
};

/**
 * Upload a document to the agent's workspace via HTTP multipart.
 * The server indexes text-based files into knowledge memory automatically.
 */
export async function uploadDocument(
  baseUrl: string,
  token: string,
  file: File,
): Promise<UploadResult | null> {
  const form = new FormData();
  form.append("file", file);

  try {
    const resp = await fetch(`${baseUrl}/api/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: resp.statusText }));
      console.error("Upload failed:", err);
      return null;
    }
    return (await resp.json()) as UploadResult;
  } catch (err) {
    console.error("Upload error:", err);
    return null;
  }
}

export async function abortChatRun(state: ChatState): Promise<boolean> {
  if (!state.client || !state.connected) return false;
  const runId = state.chatRunId;
  try {
    await state.client.request(
      "chat.abort",
      runId
        ? { sessionKey: state.sessionKey, runId }
        : { sessionKey: state.sessionKey },
    );
    return true;
  } catch (err) {
    state.lastError = String(err);
    return false;
  }
}

export function handleChatEvent(
  state: ChatState,
  payload?: ChatEventPayload,
) {
  if (!payload) return null;
  if (payload.sessionKey !== state.sessionKey) return null;

  // Final from another run (e.g. sub-agent announce): refresh history to show new message.
  // See https://github.com/openclaw/openclaw/issues/1909
  if (
    payload.runId &&
    state.chatRunId &&
    payload.runId !== state.chatRunId
  ) {
    if (payload.state === "final") return "final";
    return null;
  }

  if (payload.state === "delta") {
    const next = extractText(payload.message);
    if (typeof next === "string") {
      const current = state.chatStream ?? "";
      if (!current || next.length >= current.length) {
        state.chatStream = next;
      }
    }
  } else if (payload.state === "final") {
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
  } else if (payload.state === "aborted") {
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
  } else if (payload.state === "error") {
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
    state.lastError = payload.errorMessage ?? "chat error";
  }
  return payload.state;
}
